import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { Task } from '../types/task';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Button, Paper, Stack, Alert, Collapse, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Tooltip, IconButton, Snackbar, Checkbox } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { EditableTable } from './tables/EditableTable';
import { SegmentationTable } from './tables/SegmentationTable';
import { DocumentContent } from './document/DocumentContent';
import { useAuth } from '../contexts/AuthContext';

interface TaskCardProps {
    task: Task;
    onTaskCompleted?: (task_id: string) => void;
    selectionMode?: boolean;
    isSelected?: boolean;
    onToggleSelection?: () => void;
}

// Responsive table for consistency_check
const ConsistencyCheckTable: React.FC<{ value: Record<string, any> }> = ({ value }) => {
    const theme = useTheme();
    const bg = theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fafafa';
    const border = theme.palette.divider;
    const color = theme.palette.text.primary;
    return (
        <Box sx={{ mt: 1 }}>
            <table style={{ width: '100%', background: bg, color, borderCollapse: 'collapse', fontSize: 14 }}>
                <tbody>
                    {Object.entries(value).map(([k, v]) => (
                        <tr key={k}>
                            <td style={{ border: `1px solid ${border}`, padding: 4, fontWeight: 500, width: '30%' }}>{k}</td>
                            <td style={{ border: `1px solid ${border}`, padding: 4 }}>{typeof v === 'object' ? JSON.stringify(v, null, 1) : String(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );
};

const TaskCard: React.FC<TaskCardProps> = ({ 
    task, 
    onTaskCompleted,
    selectionMode = false,
    isSelected = false,
    onToggleSelection = () => {}
}) => {
    const { taskService } = useAuth();
    const [showMetadata, setShowMetadata] = useState(false);
    const [editedData, setEditedData] = useState<Record<string, any>>({});
    const [editedSourceReferences, setEditedSourceReferences] = useState<Record<string, any>>({});
    const [editedMetadata, setEditedMetadata] = useState<any[]>([]);
    const [hasEdits, setHasEdits] = useState(false);
    const [hasSourceEdits, setHasSourceEdits] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [mismatchedKeys, setMismatchedKeys] = useState<string[]>([]);
    const [copySnackbar, setCopySnackbar] = useState({
        open: false,
        message: ''
    });
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('instructed_to_reject');

    const checkKeyMismatches = (ai_result: any, original_request: any) => {
        if (!ai_result?.result || !original_request?.metadata?.data_points) return [];
        
        // Get keys from AI result, handling both direct values and objects with 'value' property
        const aiKeys = Object.keys(ai_result.result);
        
        // Get expected keys from metadata
        const expectedKeys = original_request.metadata.data_points;
        
        // Return keys that don't match any of the expected keys
        return aiKeys.filter(key => !expectedKeys.includes(key));
    };

    useEffect(() => {
        if (task.ai_result && task.original_request) {
            const mismatches = checkKeyMismatches(task.ai_result, task.original_request);
            setMismatchedKeys(mismatches);
        }
        
        // Initialize edited source references with the original source references
        if (task.source_references) {
            setEditedSourceReferences({...task.source_references});
        }
        
        // Initialize edited metadata for revenue segmentation table
        if (task.document_type === 'revenue_segmentation_table_extraction' && 
            task.ai_result?.result?.metadata?.value) {
            setEditedMetadata([...task.ai_result.result.metadata.value]);
        }
    }, [task.ai_result, task.original_request, task.source_references, task.document_type]);

    const handleAcceptAI = async () => {
        if (hasEdits || hasSourceEdits) {
            alert('You have made edits. Please use Submit Edited Response instead.');
            return;
        }
        try {
            await taskService.reviewTask(task.task_id, task.ai_result, 1.0);
            setShowSuccess(true);
            setTimeout(() => {
                onTaskCompleted?.(task.task_id);
            }, 1500);
        } catch (error) {
            alert('Failed to accept AI response');
        }
    };
    
    const handleRejectTask = async () => {
        try {
            await taskService.rejectTask(task.task_id, rejectionReason);
            setShowSuccess(true);
            setRejectDialogOpen(false);
            setTimeout(() => {
                onTaskCompleted?.(task.task_id);
            }, 1500);
        } catch (error) {
            alert('Failed to reject task');
        }
    };
    
    const openRejectDialog = () => {
        setRejectDialogOpen(true);
    };

    const handleSubmitEdited = async () => {
        if (!hasEdits && !hasSourceEdits) {
            alert('No edits detected. Use Accept AI Response instead.');
            return;
        }
        try {
            // Create a new result object that includes source references nested within each key
            const resultWithSources: Record<string, any> = {};
            console.log('Edited data in taskcard:', editedData);
            
            // Process the edited data and include source references
            Object.entries(editedData).forEach(([key, value]) => {
                if (!key) return; // Skip empty keys
                
                // Check if there's a source reference for this key
                const sourceRef = editedSourceReferences[key];
                
                if (sourceRef) {
                    // If there's a source reference, include it in the result
                    resultWithSources[key] = {
                        value: value,
                        source_text: sourceRef.source_text || '',
                        location: sourceRef.location || '',
                        source_url: sourceRef.source_url || ''
                    };
                    console.log('Source reference for key:', key, sourceRef);
                } else {
                    // If no source reference, just include the value
                    resultWithSources[key] = value;
                }
            });
            
            // For revenue segmentation table, ensure metadata is included
            if (task.document_type === 'revenue_segmentation_table_extraction' && editedMetadata.length > 0) {
                resultWithSources.metadata = {
                    value: editedMetadata
                };
            }
            
            const response = {
                ai_processed: true,
                result: resultWithSources,
                confidence_score: task.ai_result?.confidence_score || 0.8
            };
            
            await taskService.reviewTask(task.task_id, response, 0.8);
            setShowSuccess(true);
            setTimeout(() => {
                onTaskCompleted?.(task.task_id);
            }, 1500);
        } catch (error) {
            alert('Failed to submit edited response');
        }
    };

    const renderValue = (value: any, key?: string) => {
    if (Array.isArray(value)) {
        return (
            <Box sx={{ pl: 2 }}>
                {value.map((item, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">[{idx}]</Typography>
                        {renderValue(item)}
                    </Box>
                ))}
            </Box>
        );
    }
    if (value && typeof value === 'object') {
        return (
            <Box sx={{ pl: 2, borderLeft: '2px solid #eee', mb: 1 }}>
                {Object.entries(value).map(([k, v]) => (
                    <Box key={k} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary"><b>{k}:</b></Typography>
                        {renderValue(v, k)}
                    </Box>
                ))}
            </Box>
        );
    }
    return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {String(value)}
        </Typography>
    );
};

const renderAIResults = () => {
        if (!task.ai_result?.result) return null;

        if (task.document_type === 'chart_data_extraction') {
    const result = hasEdits ? editedData : task.ai_result.result;
    // Extract fields
    const { series = [], ...otherFields } = result || {};

    // --- Flatten chart series to long format ---
    // Each row: { label, color, x, y, seriesIdx, pointIdx }
    function flattenSeriesToRows(seriesArr: any[]): any[] {
        const rows: any[] = [];
        seriesArr.forEach((s, seriesIdx) => {
            if (Array.isArray(s.data)) {
                s.data.forEach((pt: any, pointIdx: number) => {
                    rows.push({
                        label: s.label,
                        color: s.color,
                        x: pt.x,
                        y: pt.y,
                        seriesIdx,
                        pointIdx
                    });
                });
            }
        });
        return rows;
    }
    // Convert long format rows back to series array
    function rebuildSeriesFromRows(rows: any[]): any[] {
        const bySeries: {[idx: number]: any} = {};
        rows.forEach(row => {
            if (!bySeries[row.seriesIdx]) {
                bySeries[row.seriesIdx] = { label: row.label, color: row.color, data: [] };
            }
            bySeries[row.seriesIdx].data[row.pointIdx] = { x: row.x, y: row.y };
        });
        // Fill in missing data arrays
        Object.values(bySeries).forEach((s: any) => {
            s.data = s.data.filter((d: any) => d !== undefined);
        });
        // Return as array, sorted by seriesIdx
        return Object.entries(bySeries)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([_, v]) => v);
    }
    // Columns for long format
    const longSeriesColumns = [
        { field: 'label', headerName: 'Label', width: 120, editable: false },
        { field: 'color', headerName: 'Color', width: 120, editable: false },
        { field: 'x', headerName: 'X', width: 120, editable: true },
        { field: 'y', headerName: 'Y', width: 120, editable: true },
    ];

    return (
        <Box>
            {/* Editable fields for non-series chart info */}
            {Object.entries(otherFields).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{key}</Typography>
                    {typeof value === 'object' && value !== null ? (
                        key === 'consistency_check' ? (
                            <ConsistencyCheckTable value={value} />
                        ) : (
                            <Paper variant="outlined" sx={{ p: 1, mt: 1, background: '#222', color: '#fff', fontSize: 14 }}>
                                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>
                            </Paper>
                        )
                    ) : (
                        <TextField
                            fullWidth
                            value={editedData[key] !== undefined ? editedData[key] : value || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEditedData({ ...editedData, [key]: e.target.value });
                                setHasEdits(true);
                            }}
                            variant="outlined"
                            size="small"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>
            ))}
            {/* EditableTable for series data (long format) */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>Series</Typography>
            <EditableTable
                data={flattenSeriesToRows(editedData.series || series || [])}
                columns={longSeriesColumns}
                onChange={rows => {
                    // Convert edited rows back to series structure
                    const rebuilt = rebuildSeriesFromRows(rows);
                    setEditedData({ ...editedData, series: rebuilt });
                    setHasEdits(true);
                }}
                readOnly={false}
            />
        </Box>
    );
}

if (task.document_type === 'revenue_segmentation_table_extraction') {
            const segmentData = task.ai_result.result.data?.value || [];
            return (
                <>
                    <Typography variant="h6">Revenue Segmentation Data</Typography>
                    <SegmentationTable
                        data={segmentData}
                        onChange={(newData) => {
                            setEditedData({
                                ...editedData,
                                data: { value: newData },
                            });
                            setHasEdits(true);
                        }}
                        title={`Task-${task.task_id}-Revenue-Segmentation`}
                    />
                </>
            );
        }

        if (task.document_type === 'lease') {
            const leaseData = task.ai_result.result.data?.value || [];
            return (
                <>
                    <Typography variant="h6">Lease Data</Typography>
                    <SegmentationTable
                        data={leaseData}
                        onChange={(newData) => {
                            setEditedData({
                                ...editedData,
                                data: { value: newData },
                            });
                            setHasEdits(true);
                        }}
                        title={`Task-${task.task_id}-Lease-Data`}
                    />
                </>
            );
        }

        const rawData = hasEdits ? editedData : task.ai_result.result;
        const keyValuePairs = Object.entries(rawData).map(([key, value]) => {
            const actualValue = value && typeof value === 'object' && 'value' in value 
                ? value.value 
                : value;
        
            const sourceRef = editedSourceReferences[key] || task.source_references?.[key];
        
            return {
                Key: key,
                Value: actualValue,
                isKeyMismatched: task.document_type !== 'credit_card' && task.document_type !== 'earnings_search' && mismatchedKeys.includes(key),
                source_text: sourceRef?.source_text,
                location: sourceRef?.location,
                source_url: sourceRef?.source_url
            };
        });

        return (
            <>
                {mismatchedKeys.length > 0 && task.document_type !== 'earnings_search' && task.document_type !== 'credit_card' && (
                    <div className="key-mismatch-warning">
                        <Alert role="alert" severity="warning" sx={{ mb: 2 }}>
                            <strong>Keys mismatch detected:</strong> The highlighted keys in the table below do not match the expected datapoints. 
                            Please correct the highlighted keys to match exactly with the metadata datapoints (case-sensitive).
                        </Alert>
                    </div>
                )}
                <EditableTable
                    data={keyValuePairs}
                    onChange={(newData) => {
                        console.log('New data from TaskCard:', newData);
                    
                        // Create a brand new object for our data
                        const newEditedData: Record<string, any> = {};
                        const newEditedSourceRefs: Record<string, any> = {};
                    
                        // Process each row, properly handling both normal fields and key changes
                        newData.forEach(({ Key, Value, _originalKey, source_text, location, source_url, isKeyMismatched, ...rest }) => {
                            // Skip rows with empty keys
                            if (!Key) return;
                            
                            // Use the new key name for the data
                            newEditedData[Key] = Value;
                    
                            // Only add to sourceReferences if there's actual content
                            if (source_text || location || source_url) {
                                newEditedSourceRefs[Key] = {
                                    source_text: source_text || '',
                                    location: location || '',
                                    source_url: source_url || ''
                                };
                            }
                        });
                    
                        setEditedData(newEditedData);
                        setEditedSourceReferences(newEditedSourceRefs);
                        setHasEdits(true);
                        setHasSourceEdits(true);
                    
                        // Recheck mismatches after edits
                        if (task.original_request?.metadata?.data_points) {
                            const expectedKeys = task.original_request.metadata.data_points;
                            const updatedMismatches = Object.keys(newEditedData).filter(
                                key => !expectedKeys.includes(key)
                            );
                            setMismatchedKeys(updatedMismatches);
                        }
                    }}
                    rowStyle={(row) => ({
                        backgroundColor: row.isKeyMismatched ? '#ffebee' : 'inherit'
                    })}
                    documentType={task.document_type}
                    title={`Task-${task.task_id}-${task.document_type}`}
                />
            </>
        );
    };

    return (
        <Accordion 
            sx={{
                borderLeft: isSelected ? '4px solid' : 'none',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                boxShadow: isSelected ? '0 0 5px rgba(25, 118, 210, 0.3)' : undefined
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {selectionMode && (
                        <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                                e.stopPropagation(); // Prevent accordion from toggling
                                onToggleSelection();
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent accordion from toggling
                            sx={{ mr: 1 }}
                        />
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ 
                            userSelect: 'text',
                            fontFamily: '"Plus Jakarta Sans", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                        }}>
                            Task:<Box component="span" sx={{ ml: 0.5, userSelect: 'all', display: 'inline', fontWeight: 500 }}>{task.task_id}</Box>
                            <Box component="span" sx={{ mx: 1, color: 'text.secondary', fontWeight: 400 }}>|</Box>
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                Created: {task.created_at}
                            </Box>
                        </Typography>

                    </Box>
                    <Tooltip title="Copy task ID">
                        <IconButton 
                            size="small" 
                            sx={{ ml: 1 }}
                            aria-label="Copy task ID"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation(); // Prevent accordion from toggling
                                navigator.clipboard.writeText(task.task_id);
                                setCopySnackbar({
                                    open: true,
                                    message: 'Task ID copied to clipboard!'
                                });
                            }}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy full task title">
                        <IconButton 
                            size="small" 
                            sx={{ ml: 1 }}
                            aria-label="Copy full task title"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation(); // Prevent accordion from toggling
                                navigator.clipboard.writeText(`Task ${task.task_id} - ${task.document_type} (Created: ${task.created_at})`);
                                setCopySnackbar({
                                    open: true,
                                    message: 'Full task title copied to clipboard!'
                                });
                            }}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
                    <Typography>Status: {task.status}</Typography>
                    {/* Show SEC ticker if present, as normal text */}
                    {task.document_type === 'sec' && ((task.original_request?.metadata?.ticker || task.metadata?.ticker) ? (
                        <Typography>
                            Ticker: {task.original_request?.metadata?.ticker || task.metadata?.ticker}
                        </Typography>
                    ) : null)}
                    <Typography variant="h6">Document Information</Typography>
                    <Typography>Document Type: {task.document_type}</Typography>
                    <DocumentContent task={task} />

                    {task.ai_result && (
                        <>
                            <Typography variant="h6">AI Processing Results</Typography>
                            {renderAIResults()}

                            {/* Add source references display - only for revenue segmentation table */}
                            {task.source_references && (task.document_type === 'revenue_segmentation_table_extraction' || task.document_type === 'lease') && (
                                <>
                                    <Typography variant="h6" sx={{ mt: 2 }}>Source References</Typography>
                                    <Box>
                                        <Typography>Source Information:</Typography>
                                        {task.ai_result?.result?.metadata?.value && (
                                            <EditableTable
                                                data={editedMetadata.map((item: any, index: number) => ({
                                                    ...item,
                                                    id: index // Ensure each row has an id
                                                }))}
                                                readOnly={false}
                                                onChange={(newData) => {
                                                    // Update the edited metadata state
                                                    setEditedMetadata(newData.map(item => {
                                                        // Remove the id field as it's not part of the original data structure
                                                        const { id, ...rest } = item;
                                                        return rest;
                                                    }));
                                                    
                                                    // Create a deep copy of the edited data
                                                    const updatedData = {
                                                        ...editedData,
                                                        metadata: { 
                                                            value: newData.map(item => {
                                                                // Remove the id field as it's not part of the original data structure
                                                                const { id, ...rest } = item;
                                                                return rest;
                                                            })
                                                        }
                                                    };
                                                    setEditedData(updatedData);
                                                    setHasEdits(true);
                                                }}
                                                columns={[
                                                    { field: 'source_text', headerName: 'Source Text', width: 400, editable: true },
                                                    { field: 'id', headerName: 'ID', width: 100, editable: true },
                                                    { field: 'location', headerName: 'Location', width: 200, editable: true }
                                                ]}
                                                title={`Task-${task.task_id}-Source-References`}
                                            />
                                        )}
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    <Stack direction="row" spacing={2} mt={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAcceptAI}
                            disabled={hasEdits || hasSourceEdits}
                        >
                            Accept AI Response
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleSubmitEdited}
                            disabled={!hasEdits && !hasSourceEdits}
                        >
                            Submit Edited Response
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setShowMetadata(!showMetadata)}
                        >
                            {showMetadata ? 'Hide' : 'Show'} additional metadata
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={openRejectDialog}
                        >
                            Reject Task
                        </Button>
                    </Stack>

                    {showMetadata && task.metadata && (
                        <Paper sx={{ p: 2 }}>
                            <pre>{JSON.stringify(task.metadata, null, 2)}</pre>
                        </Paper>
                    )}

                    <Collapse in={showSuccess}>
                        <Alert 
                            severity="success"
                            sx={{ 
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                zIndex: 1000
                            }}
                        >
                            Response submitted successfully!
                        </Alert>
                    </Collapse>
                    

                    {/* Snackbar for copy feedback */}
                    <Snackbar
                        open={copySnackbar.open}
                        autoHideDuration={2000}
                        onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
                        message={copySnackbar.message}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                    {/* Reject Task Dialog */}
                    <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
                        <DialogTitle>Reject Task</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Please select a reason for rejecting this task:
                            </DialogContentText>
                            <FormControl component="fieldset" sx={{ mt: 2 }}>
                                <RadioGroup
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                >
                                    <FormControlLabel 
                                        value="instructed_to_reject" 
                                        control={<Radio />} 
                                        label="I was instructed to reject this request" 
                                    />
                                    <FormControlLabel 
                                        value="not_qualified" 
                                        control={<Radio />} 
                                        label="I am not qualified for this task" 
                                    />
                                </RadioGroup>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleRejectTask} color="error" variant="contained">
                                Reject Task
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default TaskCard;
